import React, { useState, useEffect } from 'react';
import { Modal, Container, Card, Form, Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { editPresenceDetails } from '../../../redux/slices/presenceSlice';
import { updatePauseTimes, deletePause, startPause, fetchPauseReasons } from '../../../redux/slices/pausesSlice';
import HeaderMobile from "../../reusableComps/HeaderMobile/HeaderMobile";
import AddPresenceManual from '../AddPresenceManual/AddPresenceManual';
import { getTimeLogErrorMessage } from '../../../utils/timeLogging';

function getDatePart(dateTimeStr) {
    return dateTimeStr ? dateTimeStr.split('T')[0] : '';
}
function getTimePart(dateTimeStr) {
    return dateTimeStr ? dateTimeStr.split('T')[1]?.slice(0, 5) : '';
}
function combineDateTime(date, time) {
    return `${date}T${time}`;
}

const WORK_LOCATIONS = [
    { value: "Work", label: "Work" },
    { value: "Meeting", label: "Meeting" }
];

const HoursModal = ({ selectedDay, handleClose, presencesForDay, refreshPresences, variant }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const pauseReasons = useSelector((state) => state.pauses.reasons);
    const [showAddPresenceManual, setShowAddPresenceManual] = useState(false);
    const showDateFields = true;

    const [editStates, setEditStates] = useState(
        presencesForDay.map(p => ({
            id: p.id,
            check_in_date: p.check_in ? getDatePart(p.check_in) : "",
            check_in: p.check_in ? getTimePart(p.check_in) : "",
            check_out_date: p.check_out ? getDatePart(p.check_out) : "",
            check_out: p.check_out ? getTimePart(p.check_out) : "",
            work_location: p.type || "Work",
            pauses: (p.pauses || []).map(pause => ({
                id: pause.id,
                start_date: pause.start ? getDatePart(pause.start) : "",
                start: pause.start ? getTimePart(pause.start) : "",
                end_date: pause.end ? getDatePart(pause.end) : "",
                end: pause.end ? getTimePart(pause.end) : "",
                reason: pause.reason || ""
            })),
            deletedPauses: []
        }))
    );

    useEffect(() => {
        dispatch(fetchPauseReasons());
    }, [dispatch]);

    useEffect(() => {
        setEditStates(
            presencesForDay.map(p => ({
                id: p.id,
                check_in_date: p.check_in ? getDatePart(p.check_in) : "",
                check_in: p.check_in ? getTimePart(p.check_in) : "",
                check_out_date: p.check_out ? getDatePart(p.check_out) : "",
                check_out: p.check_out ? getTimePart(p.check_out) : "",
                work_location: p.type || "Work",
                pauses: (p.pauses || []).map(pause => ({
                    id: pause.id,
                    start_date: pause.start ? getDatePart(pause.start) : "",
                    start: pause.start ? getTimePart(pause.start) : "",
                    end_date: pause.end ? getDatePart(pause.end) : "",
                    end: pause.end ? getTimePart(pause.end) : "",
                    reason: pause.reason || ""
                })),
                deletedPauses: []
            }))
        );
    }, [presencesForDay]);

    const [pauseErrors, setPauseErrors] = useState(
        presencesForDay.map(p => ({
            pauses: (p.pauses || []).map(() => ({ start: "", end: "" }))
        }))
    );

    const [checkErrors, setCheckErrors] = useState(
        presencesForDay.map(() => ({ check_in: "", check_out: "" }))
    );

    const handleRemovePause = async (presenceIdx, pauseIdx) => {
        const pauseToRemove = editStates[presenceIdx].pauses[pauseIdx];
        if (pauseToRemove.id) {
            try {
                await dispatch(deletePause(pauseToRemove.id)).unwrap();
            } catch (error) {
                alert(t('components.HoursModal.error_remove_pause'));
                return;
            }
        }
        setEditStates(prev =>
            prev.map((item, i) => {
                if (i === presenceIdx) {
                    const updatedPauses = item.pauses.filter((_, idx) => idx !== pauseIdx);
                    return { ...item, pauses: updatedPauses };
                }
                return item;
            })
        );
    };

    const addPause = (presenceIdx) => {
        setEditStates(prev =>
            prev.map((item, i) => {
                if (i === presenceIdx) {
                    const newPause = { start_date: "", start: "", end_date: "", end: "", reason: "" };
                    return { ...item, pauses: [...item.pauses, newPause] };
                }
                return item;
            })
        );
    };

    const toMinutes = (timeStr) => {
        const [h, m] = timeStr.split(':').map(Number);
        return h * 60 + m;
    };

    const getDateTimeLocal = (date, time) => date && time ? `${date}T${time}` : "";

    const handleInputChange = (idx, field, value) => {
        setEditStates(prev =>
            prev.map((item, i) => {
                if (i === idx) {
                    let updatedItem = { ...item };
                    if (showDateFields && (field === "check_in" || field === "check_out")) {
                        let date = "";
                        let time = "";
                        if (value && value.includes('T')) {
                            [date, time] = value.split('T');
                        } else if (value) {
                            time = value;
                            date = field === "check_in" ? updatedItem.check_in_date : updatedItem.check_out_date;
                        }
                        if (field === "check_in") {
                            updatedItem.check_in_date = date;
                            updatedItem.check_in = time;
                        } else {
                            updatedItem.check_out_date = date;
                            updatedItem.check_out = time;
                        }
                    } else {
                        updatedItem[field] = value;
                    }

                    const checkIn = updatedItem.check_in;
                    const checkOut = updatedItem.check_out;
                    const checkInDate = updatedItem.check_in_date;
                    const checkOutDate = updatedItem.check_out_date;

                    let errorMsgCheckIn = "";
                    let errorMsgCheckOut = "";

                    if (checkIn && checkOut) {
                        if (showDateFields) {
                            const checkInFull = new Date(`${checkInDate}T${checkIn}`);
                            const checkOutFull = new Date(`${checkOutDate}T${checkOut}`);
                            if (checkOutFull < checkInFull) {
                                errorMsgCheckOut = t('components.HoursModal.check_out_before_check_in');
                            }
                            if (field === "check_out") {
                                for (const pause of item.pauses) {
                                    if (pause.end && pause.end_date) {
                                        const pauseEndFull = new Date(`${pause.end_date}T${pause.end}`);
                                        if (checkOutFull < pauseEndFull) {
                                            errorMsgCheckOut = t('components.HoursModal.check_out_before_pause_end');
                                            break;
                                        }
                                    }
                                }
                            }
                        } else {
                            if (toMinutes(checkOut) < toMinutes(checkIn)) {
                                errorMsgCheckOut = t('components.HoursModal.check_out_before_check_in');
                            }
                            if (field === "check_out") {
                                for (const pause of item.pauses) {
                                    if (pause.end && toMinutes(checkOut) < toMinutes(pause.end)) {
                                        errorMsgCheckOut = t('components.HoursModal.check_out_before_pause_end');
                                        break;
                                    }
                                }
                            }
                        }
                    }

                    setCheckErrors(prevErrors =>
                        prevErrors.map((errItem, errIdx) => {
                            if (errIdx === idx) {
                                return { check_in: errorMsgCheckIn, check_out: errorMsgCheckOut };
                            }
                            return errItem;
                        })
                    );

                    return updatedItem;
                }
                return item;
            })
        );
    };

    const handleWorkLocationChange = (idx, value) => {
        setEditStates(prev =>
            prev.map((item, i) =>
                i === idx ? { ...item, work_location: value } : item
            )
        );
    };

    const handlePauseChange = (presenceIdx, pauseIdx, field, value) => {
        setEditStates(prev =>
            prev.map((item, i) => {
                if (i === presenceIdx) {
                    let updatedItem = { ...item };
                    let updatedPauses = [...item.pauses];
                    if (showDateFields && (field === "start" || field === "end")) {
                        let date = "";
                        let time = "";
                        if (value && value.includes('T')) {
                            [date, time] = value.split('T');
                        } else if (value) {
                            time = value;
                            date = field === "start" ? updatedPauses[pauseIdx].start_date : updatedPauses[pauseIdx].end_date;
                        }
                        if (field === "start") {
                            updatedPauses[pauseIdx].start_date = date;
                            updatedPauses[pauseIdx].start = time;
                        } else {
                            updatedPauses[pauseIdx].end_date = date;
                            updatedPauses[pauseIdx].end = time;
                        }
                    } else {
                        updatedPauses[pauseIdx][field] = value;
                    }
                    updatedItem.pauses = updatedPauses;

                    const presence = updatedItem;
                    const checkIn = presence.check_in;
                    const checkOut = presence.check_out;
                    const checkInDate = presence.check_in_date;
                    const checkOutDate = presence.check_out_date;

                    let errorMsg = "";
                    const pause = updatedPauses[pauseIdx];

                    if (showDateFields) {
                        const checkInFull = checkIn && checkInDate ? new Date(`${checkInDate}T${checkIn}`) : null;
                        const checkOutFull = checkOut && checkOutDate ? new Date(`${checkOutDate}T${checkOut}`) : null;
                        const pauseStartFull = pause.start && pause.start_date ? new Date(`${pause.start_date}T${pause.start}`) : null;
                        const pauseEndFull = pause.end && pause.end_date ? new Date(`${pause.end_date}T${pause.end}`) : null;

                        if (field === "start") {
                            if (checkInFull && pauseStartFull && pauseStartFull < checkInFull) {
                                errorMsg = t('components.HoursModal.pause_start_before_check_in');
                            }
                            if (pauseEndFull && pauseStartFull && pauseStartFull > pauseEndFull) {
                                errorMsg = t('components.HoursModal.pause_start_after_pause_end');
                            }
                        } else if (field === "end") {
                            if (checkOutFull && pauseEndFull && pauseEndFull > checkOutFull) {
                                errorMsg = t('components.HoursModal.pause_end_after_check_out');
                            }
                            if (pauseStartFull && pauseEndFull && pauseEndFull < pauseStartFull) {
                                errorMsg = t('components.HoursModal.pause_end_before_pause_start');
                            }
                        }
                    } else {
                        if (field === "start") {
                            if (checkIn && pause.start && toMinutes(pause.start) < toMinutes(checkIn)) {
                                errorMsg = t('components.HoursModal.pause_start_before_check_in');
                            }
                            if (pause.end && toMinutes(pause.start) > toMinutes(pause.end)) {
                                errorMsg = t('components.HoursModal.pause_start_after_pause_end');
                            }
                        } else if (field === "end") {
                            if (checkOut && pause.end && toMinutes(pause.end) > toMinutes(checkOut)) {
                                errorMsg = t('components.HoursModal.pause_end_after_check_out');
                            }
                            if (pause.start && toMinutes(pause.end) < toMinutes(pause.start)) {
                                errorMsg = t('components.HoursModal.pause_end_before_pause_start');
                            }
                        }
                    }

                    if (errorMsg === "") {
                        const newTime = toMinutes(field === "start" ? pause.start : pause.end);
                        const updatedPause = { ...pause, [field]: field === "start" ? pause.start : pause.end };

                        for (let j = 0; j < updatedPauses.length; j++) {
                            if (j === pauseIdx) continue;
                            const otherPause = updatedPauses[j];
                            if (!otherPause.start || !otherPause.end) continue;

                            if (showDateFields) {
                                const otherStartFull = otherPause.start_date && otherPause.start ? new Date(`${otherPause.start_date}T${otherPause.start}`) : null;
                                const otherEndFull = otherPause.end_date && otherPause.end ? new Date(`${otherPause.end_date}T${otherPause.end}`) : null;
                                const updatedStartFull = pause.start_date && pause.start ? new Date(`${pause.start_date}T${pause.start}`) : null;
                                const updatedEndFull = pause.end_date && pause.end ? new Date(`${pause.end_date}T${pause.end}`) : null;

                                if (updatedStartFull && updatedEndFull && otherStartFull && otherEndFull) {
                                    if (!(updatedEndFull <= otherStartFull || updatedStartFull >= otherEndFull)) {
                                        errorMsg = field === "start"
                                            ? t('components.HoursModal.pause_start_overlap')
                                            : t('components.HoursModal.pause_end_overlap');
                                        break;
                                    }
                                }
                            } else {
                                const otherStart = toMinutes(otherPause.start);
                                const otherEnd = toMinutes(otherPause.end);
                                const updatedStart = field === "start" ? newTime : toMinutes(updatedPause.start);
                                const updatedEnd = field === "end" ? newTime : toMinutes(updatedPause.end);

                                if (!(updatedEnd <= otherStart || updatedStart >= otherEnd)) {
                                    errorMsg = field === "start"
                                        ? t('components.HoursModal.pause_start_overlap')
                                        : t('components.HoursModal.pause_end_overlap');
                                    break;
                                }
                            }
                        }
                    }

                    setPauseErrors(prevErrors =>
                        prevErrors.map((errItem, errIdx) => {
                            if (errIdx === presenceIdx) {
                                return {
                                    pauses: errItem.pauses.map((pauseErr, pIdx) =>
                                        pIdx === pauseIdx ? { ...pauseErr, [field]: errorMsg } : pauseErr
                                    )
                                };
                            }
                            return errItem;
                        })
                    );

                    if (errorMsg === "") {
                        return updatedItem;
                    } else {
                        return item;
                    }
                }
                return item;
            })
        );
    };

    const handlePauseReasonChange = (presenceIdx, pauseIdx, value) => {
        setEditStates(prev =>
            prev.map((item, i) => {
                if (i === presenceIdx) {
                    return {
                        ...item,
                        pauses: item.pauses.map((pause, j) =>
                            j === pauseIdx ? { ...pause, reason: value } : pause
                        )
                    };
                }
                return item;
            })
        );
    };

    const handleSave = async (idx) => {
        const { id, check_in, check_in_date, check_out, check_out_date, pauses, work_location } = editStates[idx];
        const toISO = (datePart, timePart) => (timePart ? new Date(combineDateTime(datePart, timePart)).toISOString() : null);
        try {
            await dispatch(editPresenceDetails({
                presenceId: id,
                updates: {
                    check_in: toISO(check_in_date, check_in),
                    check_out: toISO(check_out_date, check_out),
                    type: work_location
                }
            })).unwrap();
            for (const pause of pauses) {
                if (pause.id) {
                    await dispatch(updatePauseTimes({
                        pauseId: pause.id,
                        timeData: {
                            start: toISO(pause.start_date, pause.start),
                            end: toISO(pause.end_date, pause.end),
                            reason: pause.reason || ""
                        }
                    })).unwrap();
                } else {
                    await dispatch(startPause({
                        presenceId: id.toString(),
                        reason: pause.reason,
                        start_location: "office",
                        start: toISO(pause.start_date, pause.start) || undefined,
                        end: toISO(pause.end_date, pause.end) || undefined
                    })).unwrap();
                }
            }
        } catch (err) {
            toast.error(getTimeLogErrorMessage(err, t, t('components.HoursModal.save_error')));
            if (refreshPresences) refreshPresences();
            return;
        }
        if (refreshPresences) refreshPresences();
        handleClose();
    };

    const hasPauseErrors = pauseErrors.some(presenceErr =>
        presenceErr.pauses.some(pauseErr => pauseErr.start !== "" || pauseErr.end !== "")
    );
    const hasCheckErrors = checkErrors.some(err => err.check_in !== "" || err.check_out !== "");
    const hasValidationErrors = hasPauseErrors || hasCheckErrors;

    const modalBodyStyle =
        variant === "admin"
            ? { background: "transparent" }
            : { background: "linear-gradient(to bottom, #0080ff, #2e005e)" };

    const labelClass = variant === "admin" ? "text-white" : "";

    return (
        <Modal
            show={!!selectedDay}
            onHide={handleClose}
            fullscreen={variant !== "admin"}
            size={variant === "admin" ? "lg" : undefined}
        >
            <Modal.Body style={modalBodyStyle}>
                {variant !== "admin" && (
                    <HeaderMobile
                        showArrow={true}
                        showEdit={false}
                        showDelete={false}
                        onArrow={handleClose}
                        onDelete={() => alert("Deleted!")}
                        onEdit={() => alert("Edit!")}
                    >
                        {selectedDay && selectedDay.toLocaleDateString('pt-BR')}
                    </HeaderMobile>
                )}
                <Container>
                    {presencesForDay.length === 0 && (
                        <>
                            <div className={variant === "admin" ? "text-secondary text-center" : "text-white text-center"}>
                                {t('components.HoursModal.no_presence')}
                            </div>
                            <div className="text-center mt-3">
                                <Button
                                    style={{ backgroundColor: "#17296B" }}
                                    className="fw-bold"
                                    onClick={() => setShowAddPresenceManual(true)}
                                >
                                    {t('components.HoursModal.add_presence_manual')}
                                </Button>
                            </div>
                            {showAddPresenceManual && (
                                <AddPresenceManual
                                    show={showAddPresenceManual}
                                    onHide={() => setShowAddPresenceManual(false)}
                                />
                            )}
                        </>
                    )}
                    {editStates.map((presence, idx) => (
                        <Card key={presence.id} className={variant === "admin" ? "mb-3" : "mb-3 bg-dark text-white"}>
                            <Card.Body>
                                <Form>
                                    <Form.Group>
                                        <Form.Label className={labelClass}>{t('components.HoursModal.check_in')}</Form.Label>
                                        <Form.Control
                                            type="datetime-local"
                                            value={getDateTimeLocal(presence.check_in_date, presence.check_in)}
                                            onChange={e => handleInputChange(idx, "check_in", e.target.value)}
                                            isInvalid={!!checkErrors[idx]?.check_in}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {checkErrors[idx]?.check_in}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group>
                                        <Form.Label className={labelClass}>{t('components.HoursModal.check_out')}</Form.Label>
                                        <Form.Control
                                            type="datetime-local"
                                            value={getDateTimeLocal(presence.check_out_date, presence.check_out)}
                                            onChange={e => handleInputChange(idx, "check_out", e.target.value)}
                                            isInvalid={!!checkErrors[idx]?.check_out}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {checkErrors[idx]?.check_out}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group>
                                        <Form.Label className={labelClass}>{t('components.HoursModal.work_location')}</Form.Label>
                                        <Form.Select
                                            value={presence.work_location}
                                            onChange={e => handleWorkLocationChange(idx, e.target.value)}
                                        >
                                            {WORK_LOCATIONS.map(loc => (
                                                <option key={loc.value} value={loc.value}>{loc.label}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                    <hr className={variant === "admin" ? "" : "bg-light"} />
                                    <div className={`mb-2 fw-bold ${labelClass}`}>
                                        {t('components.HoursModal.pauses')}
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        {presence.pauses.length === 0 && (
                                            <div className={variant === "admin" ? "text-secondary mb-2" : "text-white-50 mb-2"}>
                                                {t('components.HoursModal.no_pauses')}
                                            </div>
                                        )}
                                        <Button variant="secondary" size="sm" onClick={() => addPause(idx)}>
                                            {t('components.HoursModal.add_pause')}
                                        </Button>
                                    </div>
                                    {presence.pauses.map((pause, pauseIdx) => (
                                        <div key={pause.id || pauseIdx} className={variant === "admin" ? "mb-3 border rounded p-2" : "mb-3 border rounded p-2 bg-secondary bg-opacity-25"}>
                                            <Form.Group>
                                                <Form.Label className={labelClass}>{t('components.HoursModal.pause_start')}</Form.Label>
                                                <Form.Control
                                                    type="datetime-local"
                                                    value={getDateTimeLocal(pause.start_date, pause.start)}
                                                    onChange={e => handlePauseChange(idx, pauseIdx, "start", e.target.value)}
                                                    isInvalid={!!pauseErrors[idx]?.pauses[pauseIdx]?.start}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {pauseErrors[idx]?.pauses[pauseIdx]?.start}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Group>
                                                <Form.Label className={labelClass}>{t('components.HoursModal.pause_end')}</Form.Label>
                                                <Form.Control
                                                    type="datetime-local"
                                                    value={pause.end_date && pause.end ? getDateTimeLocal(pause.end_date, pause.end) : ""}
                                                    onChange={e => handlePauseChange(idx, pauseIdx, "end", e.target.value)}
                                                    isInvalid={!!pauseErrors[idx]?.pauses[pauseIdx]?.end}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {pauseErrors[idx]?.pauses[pauseIdx]?.end}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Group>
                                                <Form.Label className={labelClass}>{t('components.HoursModal.reason')}</Form.Label>
                                                <Form.Select
                                                    value={pause.reason}
                                                    onChange={e => handlePauseReasonChange(idx, pauseIdx, e.target.value)}
                                                >
                                                    <option value="">{t('components.HoursModal.reason_placeholder')}</option>
                                                    {pauseReasons.map((reason, i) => (
                                                        <option key={i} value={reason}>{reason}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                            <div className="text-end mt-2">
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleRemovePause(idx, pauseIdx)}
                                                >
                                                    {t('components.HoursModal.remove_pause')}
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                    <Button
                                        className="mt-2"
                                        variant="primary"
                                        onClick={() => handleSave(idx)}
                                        disabled={hasValidationErrors}
                                    >
                                        {t('components.HoursModal.save')}
                                    </Button>
                                </Form>
                            </Card.Body>
                        </Card>
                    ))}
                </Container>
            </Modal.Body>
        </Modal>
    );
};

export default HoursModal;